import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

export default function Team() {
  const { t } = useLang();
  return (
    <section
      id="team"
      className="relative py-32 md:py-48 px-6 md:px-12 bg-[#050505] border-t hairline"
      data-testid="team-section"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 items-end">
          <div className="md:col-span-7">
            <div className="flex items-center gap-4 mb-8">
              <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[color:var(--muted)]">
                {t.team.label}
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-display uppercase text-5xl md:text-7xl lg:text-8xl font-extrabold leading-[0.9] tracking-[-0.03em] whitespace-pre-line"
            >
              {t.team.title}
            </motion.h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-base md:text-lg text-[color:var(--muted)] leading-relaxed">
              {t.team.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-4">
          {t.team.members.map((m, i) => {
            const CardTag = m.link ? "a" : "div";
            const linkProps = m.link
              ? {
                  href: m.link,
                  target: "_blank",
                  rel: "noopener noreferrer",
                  "data-cursor": "hover",
                }
              : {};
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="team-card group"
                data-testid={`team-member-${i}`}
              >
                <CardTag
                  {...linkProps}
                  className="block no-underline"
                  data-testid={`team-member-link-${i}`}
                >
                  <div className="aspect-[3/4] overflow-hidden mb-4">
                    <img
                      src={m.image}
                      alt={m.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--muted)] mb-1">
                    {m.origin}
                  </div>
                  <div className="font-display font-bold text-lg tracking-tight leading-tight no-underline transition-colors duration-300 group-hover:text-[color:var(--accent)] inline-flex items-center gap-2">
                    {m.name}
                    {m.link && (
                      <span
                        aria-hidden
                        className="text-xs opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0"
                      >
                        ↗
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-[color:var(--muted)] font-serif-italic mt-1">
                    {m.role}
                  </div>
                </CardTag>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
